import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, DatePicker, Input, InputNumber, Message, Modal, Space, Table, Tag, Tooltip, Typography } from "@arco-design/web-react";
import dayjs from "dayjs";

import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, OverwriteApi, DeleteApi, PostApi } from "../Api/AuthenticatedApi";
import { grantForm, grantLive, grantRequest, grantSummary } from "./grantHelpers";

const { Text, Paragraph } = Typography;

const STRIPE_STATUS = {
  active: { label: "Paying", color: "green" },
  trialing: { label: "Trial", color: "green" },
  past_due: { label: "Payment failed", color: "red" },
  unpaid: { label: "Payment failed", color: "red" },
  canceled: { label: "Cancelled", color: "gray" },
  comp: { label: "Deal (set in database)", color: "arcoblue" },
};

// Every organization, for platform admins: give one access outside Stripe
// (for testing, or a deal worked out by hand), or disable it. Platform-level: the API gates every route on the admin allowlist, and this
// screen is only linked when /api/me says so.
export default function BillingAccess() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  // The org whose access is being edited, and the form.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(grantForm(null));
  const [saving, setSaving] = useState(false);

  // The org being disabled, and why.
  const [disabling, setDisabling] = useState(null);
  const [reason, setReason] = useState("");
  // The admin's own orgs, which they can't disable (the API refuses too).
  const [myOrgIds, setMyOrgIds] = useState([]);
  useEffect(() => {
    FetchApi(getAccessTokenSilently, "/api/me",
      (data) => setMyOrgIds(((data && data.organizations) || []).map((o) => o.id)),
      () => {}, () => {});
  }, [getAccessTokenSilently]);

  const load = useCallback(() => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, "/api/admin/billing/orgs?q=" + encodeURIComponent(query),
      (data) => {
        setLoading(false);
        if (Array.isArray(data)) { setOrgs(data); setForbidden(false); }
        else { setForbidden(true); }
      },
      () => navigate("/login"),
      () => { setLoading(false); setForbidden(true); });
  }, [getAccessTokenSilently, navigate, query]);

  useEffect(load, [load]);

  const openGrant = (org) => {
    setEditing(org);
    setForm(grantForm(grantLive(org.grant) ? org.grant : null));
  };

  const save = () => {
    setSaving(true);
    OverwriteApi(getAccessTokenSilently, `/api/admin/billing/orgs/${editing.organization_id}/grant`,
      () => {
        setSaving(false);
        setEditing(null);
        Message.success(`Access granted to ${editing.name || "the organization"}`);
        load();
      },
      () => navigate("/login"),
      (err) => { setSaving(false); Message.error((err && err.message) || "Couldn't grant access"); },
      grantRequest(form));
  };

  const revoke = (org) => {
    Modal.confirm({
      title: `Remove ${org.name || "this organization"}'s access?`,
      content: "They go back to their Stripe plan, or the free plan if they don't pay. Usage while they had access is never billed.",
      okText: "Remove access",
      okButtonProps: { status: "danger" },
      onOk: () => new Promise((resolve, reject) => {
        DeleteApi(getAccessTokenSilently, `/api/admin/billing/orgs/${org.organization_id}/grant`,
          () => { Message.success("Access removed"); load(); resolve(); },
          () => { reject(); navigate("/login"); },
          (err) => { Message.error((err && err.message) || "Couldn't remove access"); reject(err); });
      }),
    });
  };

  const disable = () => {
    setSaving(true);
    PostApi(getAccessTokenSilently, `/api/admin/billing/orgs/${disabling.organization_id}/disable`,
      () => {
        setSaving(false);
        setDisabling(null);
        Message.success(`${disabling.name || "The organization"} is disabled`);
        load();
      },
      () => navigate("/login"),
      (err) => { setSaving(false); Message.error((err && err.message) || "Couldn't disable the account"); },
      { reason });
  };

  const enable = (org) => {
    Modal.confirm({
      title: `Enable ${org.name || "this organization"} again?`,
      content: "Its members can use the app again, it can send, and its forms take signups.",
      okText: "Enable",
      onOk: () => new Promise((resolve, reject) => {
        DeleteApi(getAccessTokenSilently, `/api/admin/billing/orgs/${org.organization_id}/disable`,
          () => { Message.success("Enabled again"); load(); resolve(); },
          () => { reject(); navigate("/login"); },
          (err) => { Message.error((err && err.message) || "Couldn't enable the account"); reject(err); });
      }),
    });
  };

  if (forbidden) {
    return (
      <Content>
        <Alert type="warning" content="This page is for platform admins only." />
      </Content>
    );
  }

  const columns = [
    {
      title: "Organization",
      render: (_, o) => (
        <div>
          <div style={{ fontWeight: 600 }}>{o.name || "(no name)"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{(o.emails || []).join(", ") || "no members"}</Text>
        </div>
      ),
    },
    {
      title: "Stripe",
      render: (_, o) => {
        const s = STRIPE_STATUS[o.status];
        if (!s) return <Text type="secondary">Free plan</Text>;
        return <Space size={4}><Tag color={s.color}>{s.label}</Tag>{o.plan && <Text type="secondary">{o.plan}</Text>}</Space>;
      },
    },
    { title: "Contacts", dataIndex: "contacts_now", render: (n) => Number(n || 0).toLocaleString() },
    { title: "Emails this month", dataIndex: "emails_this_month", render: (n) => Number(n || 0).toLocaleString() },
    {
      title: "Granted access",
      render: (_, o) => {
        if (!o.grant) return <Text type="secondary">None</Text>;
        const live = grantLive(o.grant);
        return (
          <div>
            <Tag color={live ? "arcoblue" : "gray"}>{live ? "Granted" : "Ended"}</Tag>{" "}
            <Text style={{ fontSize: 13 }}>{grantSummary(o.grant)}</Text>
            {o.grant.note && <div><Text type="secondary" style={{ fontSize: 12 }}>{o.grant.note}</Text></div>}
          </div>
        );
      },
    },
    {
      title: "",
      render: (_, o) => (
        <Space>
          <Button size="small" type="primary" onClick={() => openGrant(o)}>
            {grantLive(o.grant) ? "Change" : "Grant access"}
          </Button>
          {o.grant && <Button size="small" onClick={() => revoke(o)}>Remove</Button>}
        </Space>
      ),
    },
    {
      title: "Account",
      render: (_, o) => {
        if (o.disabled) {
          return (
            <Space direction="vertical" size={4}>
              <Space size={6}>
                <Tag color="red">Disabled</Tag>
                <Button size="small" onClick={() => enable(o)}>Enable</Button>
              </Space>
              {o.disabled.reason && <Text type="secondary" style={{ fontSize: 12 }}>{o.disabled.reason}</Text>}
            </Space>
          );
        }
        const mine = myOrgIds.includes(o.organization_id);
        const button = (
          <Button size="small" status="danger" disabled={mine} onClick={() => { setReason(""); setDisabling(o); }}>
            Disable
          </Button>
        );
        return mine ? <Tooltip content="You belong to this organization">{button}</Tooltip> : button;
      },
    },
  ];

  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Accounts (admin)" }]} />
      <SummaryCard title="Accounts" subTitle="Grant access outside Stripe, or disable an account">
        <Paragraph type="secondary">
          Disabling an account stops it completely: its members can't use the app, it sends nothing, and its forms
          stop taking signups. Unsubscribe links in emails it already sent keep working.
        </Paragraph>
        <Paragraph type="secondary">
          While access is granted, its limits decide what the organization can send, whatever their Stripe plan says,
          and nothing they send is billed to them. If they also pay through Stripe, the monthly fee keeps charging:
          cancel it in Stripe if the deal replaces it.
        </Paragraph>
        <Input.Search
          allowClear
          placeholder="Search by organization name or member email"
          value={search}
          onChange={setSearch}
          onSearch={(v) => setQuery((v || "").trim())}
          onClear={() => setQuery("")}
          style={{ maxWidth: 420, marginBottom: 12 }}
        />
        <Table rowKey="organization_id" loading={loading} columns={columns} data={orgs} pagination={false}
          noDataElement={<Text type="secondary">No organizations match.</Text>} />
      </SummaryCard>

      <Modal
        title={editing ? `Grant access: ${editing.name || "organization"}` : ""}
        visible={!!editing}
        onCancel={() => setEditing(null)}
        onOk={save}
        okText="Grant access"
        confirmLoading={saving}
        unmountOnExit
      >
        <Space direction="vertical" style={{ width: "100%" }} size={14}>
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Emails a month</div>
            <InputNumber min={0} precision={0} value={form.emails} onChange={(v) => set({ emails: v })} style={{ width: 200 }} />
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>0 = no limit</Text>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Contacts</div>
            <InputNumber min={0} precision={0} value={form.contacts} onChange={(v) => set({ contacts: v })} style={{ width: 200 }} />
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>0 = no limit</Text>
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Last day of access</div>
            <DatePicker
              value={form.endsOn || undefined}
              format="YYYY-MM-DD"
              placeholder="No end date"
              disabledDate={(d) => d.isBefore(dayjs(), "day")}
              onChange={(v) => set({ endsOn: v || "" })}
              style={{ width: 200 }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Note (only admins see it)</div>
            <Input.TextArea value={form.note} autoSize={{ minRows: 2 }} placeholder="e.g. Beta partner, 3 months free"
              onChange={(v) => set({ note: v })} />
          </div>
        </Space>
      </Modal>

      <Modal
        title={disabling ? `Disable ${disabling.name || "this organization"}?` : ""}
        visible={!!disabling}
        onCancel={() => setDisabling(null)}
        onOk={disable}
        okText="Disable account"
        okButtonProps={{ status: "danger" }}
        confirmLoading={saving}
        unmountOnExit
      >
        <Paragraph>
          Its members won't be able to use the app, nothing will be sent, and its forms will stop taking signups.
          You can enable it again at any time.
        </Paragraph>
        <div style={{ fontSize: 12, color: "var(--color-text-3)", marginBottom: 4 }}>Reason (only admins see it)</div>
        <Input.TextArea value={reason} autoSize={{ minRows: 2 }} placeholder="e.g. Spam complaints" onChange={setReason} />
      </Modal>
    </Content>
  );
}
